import { motion } from "framer-motion";

import MessagesShimmer from "./Messages/MessagesShimmer";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { PlaygroundChatPanelSkeleton } from "@/components/Playground/Drawer/Chat/ChatSkeleton";

interface PlaygroundShimmerProps {
  className?: string;
  fullWidth?: boolean;
  isChatMode?: boolean;
}

const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const drawerVariants = {
  hidden: { y: 20, opacity: 0.7 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 16,
      mass: 0.9,
    },
  },
};

export const PlaygroundShimmer = ({
  className,
  fullWidth = false,
  isChatMode = false,
}: PlaygroundShimmerProps) => (
  <motion.div
    variants={pageVariants}
    initial="hidden"
    animate="visible"
    className={cn("flex h-full w-full flex-col overflow-hidden", className)}
  >
    {/* Messages area */}
    <div className="min-h-0 flex-1 overflow-y-auto">
      {/* Header */}
      <div className="h-[65px] w-full border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32 rounded bg-gray-100/80" />
          <div className="flex space-x-2">
            <Skeleton className="h-8 w-24 rounded-full bg-gray-100/60" />
            <Skeleton className="h-8 w-8 rounded-md bg-gray-100/60" />
            <Skeleton className="h-8 w-8 rounded-md bg-gray-100/60" />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="p-3">
        <MessagesShimmer count={3} />
      </div>
    </div>

    {/* Drawer shimmer (bottom side) */}
    {!fullWidth && (
      <motion.div
        variants={drawerVariants}
        className="relative z-10 w-full shrink-0 overflow-visible"
      >
        <div
          className={cn(
            "relative flex w-full flex-col overflow-hidden rounded-t-[18px] border border-b-0 border-gray-200 bg-white",
            "shadow-[0_-9px_20px_-16px_rgba(15,23,42,0.20)]",
          )}
          style={{ height: isChatMode ? 360 : 220 }}
        >
          {/* Drawer Drag handle indicator */}
          <div className="absolute left-1/2 top-1 flex h-5 w-16 -translate-x-1/2 items-center justify-center rounded-md">
            <span className="h-1 w-10 rounded-full bg-gray-200" />
          </div>

          {/* Control Bar Header */}
          <div className="relative flex h-[42px] shrink-0 items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-24 bg-gray-100/80" />
              <Skeleton className="h-5 w-16 rounded-full bg-gray-100/50" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-7 w-7 rounded bg-gray-100/50" />
              <Skeleton className="h-7 w-20 rounded bg-gray-100/50" />
            </div>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-hidden bg-gray-50/50 p-3">
            {isChatMode ? (
              <div className="flex h-full flex-col gap-4 p-1">
                <PlaygroundChatPanelSkeleton />
              </div>
            ) : (
              <div className="flex h-full flex-col gap-y-3">
                {/* Outputs section shimmer */}
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-5 w-5 rounded-full bg-gray-100/80" />
                        <Skeleton className="h-5 w-28 bg-gray-100/80" />
                        <Skeleton className="h-5 w-16 rounded-full bg-gray-100/50" />
                      </div>
                      <Skeleton className="h-5 w-16 bg-gray-100/50" />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-col gap-y-2">
                      <Skeleton className="h-3.5 w-[90%] rounded bg-gray-100/60" />
                      <Skeleton className="h-3.5 w-[75%] rounded bg-gray-100/50" />
                      <Skeleton className="h-3.5 w-[85%] rounded bg-gray-100/50" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    )}
  </motion.div>
);

export default PlaygroundShimmer;
